import { useState, useCallback } from 'react';

import { GenericTable } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useGetAssigneeSessionsQuery } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.api';
import type { PastSession } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';

import {
  Box,
  TableCell,
  TablePagination,
  TableRow,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { MemberTabErrorState } from './MemberTabErrorState';
import { useAgentContext } from './PastSessionsTab.agentContext';
import { TranscriptView } from './TranscriptView';
import { useCurrentMember } from './useCurrentMember';

const {
  modules: { coachingDashboard },
} = appStrings;

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const getSessionDuration = (session: PastSession): string => {
  if (session.recording?.duration != null) {
    return formatDuration(session.recording.duration);
  }

  const diff =
    (new Date(session.scheduled_end).getTime() - new Date(session.scheduled_at).getTime()) / 1000;

  return formatDuration(diff);
};

const formatDate = (isoString: string): string => {
  const date = new Date(isoString);

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const HEADERS = ['Date', 'Meeting ID', 'Duration', 'Participants', 'Transcript'];

export const PastSessionsTab = () => {
  const currentMember = useCurrentMember();
  const { member, isError: isMemberError } = currentMember;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [selectedTranscript, setSelectedTranscript] = useState<{
    recordingId: number;
    meetingDate: string;
  } | null>(null);

  useAgentContext(selectedTranscript);

  const { data, isLoading, isError } = useGetAssigneeSessionsQuery(
    member
      ? {
          userId: member.id,
          range: [page * rowsPerPage, (page + 1) * rowsPerPage - 1],
        }
      : skipToken,
  );

  const sessions = data?.data ?? [];
  const totalCount = data?.total ?? 0;

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleOpenTranscript = useCallback((session: PastSession) => {
    if (!session.recording) return;

    setSelectedTranscript({
      recordingId: session.recording.id,
      meetingDate: formatDate(session.scheduled_at),
    });
  }, []);

  const handleBackToSessions = useCallback(() => {
    setSelectedTranscript(null);
  }, []);

  if (isMemberError) return <MemberTabErrorState {...currentMember} />;

  if (!member) return null;

  if (selectedTranscript) {
    return (
      <TranscriptView
        recordingId={selectedTranscript.recordingId}
        meetingDate={selectedTranscript.meetingDate}
        onBack={handleBackToSessions}
      />
    );
  }

  if (isError) {
    return (
      <Box sx={emptyStateSx}>
        <Typography variant="text-md-regular" sx={errorTextSx}>
          {coachingDashboard.failedToLoadSessions}
        </Typography>
      </Box>
    );
  }

  if (!isLoading && sessions.length === 0) {
    return (
      <Box sx={emptyStateSx}>
        <Typography variant="text-md-regular" sx={emptyStateTextSx}>
          {coachingDashboard.noSessions}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      <GenericTable headers={HEADERS} ariaLabel="Past sessions table">
        {sessions.map((session) => (
          <TableRow key={session.id}>
            <TableCell>
              <Box>
                <Typography variant="text-sm-regular">
                  {formatDate(session.scheduled_at)}
                </Typography>
                <Typography variant="text-xs-regular" sx={productSubtitleSx}>
                  {session.product.name}
                </Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Typography variant="text-sm-regular" sx={monospaceTextSx}>
                {session.recording?.zoom_meeting_id ?? '\u2014'}
              </Typography>
            </TableCell>
            <TableCell>{getSessionDuration(session)}</TableCell>
            <TableCell>{session.invitee_name}</TableCell>
            <TableCell>
              <TranscriptCell session={session} onViewTranscript={handleOpenTranscript} />
            </TableCell>
          </TableRow>
        ))}
      </GenericTable>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        disabled={isLoading}
        sx={paginationSx}
      />
    </Box>
  );
};

const TranscriptCell = ({
  session,
  onViewTranscript,
}: Readonly<{
  session: PastSession;
  onViewTranscript: (session: PastSession) => void;
}>) => {
  if (!session.recording) {
    return (
      <Typography variant="text-sm-regular" sx={mutedTextSx}>
        {coachingDashboard.noRecording}
      </Typography>
    );
  }

  if (session.recording.has_transcript) {
    return (
      <Typography
        id={`past-session-view-transcript-${session.id}`}
        variant="text-sm-semibold"
        sx={viewLinkSx}
        onClick={() => onViewTranscript(session)}
        role="button"
        tabIndex={0}
      >
        {coachingDashboard.view}
      </Typography>
    );
  }

  switch (session.recording.status) {
    case 'no_recording':
      return (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {coachingDashboard.noRecording}
        </Typography>
      );
    case 'no_transcript':
    case 'failed':
      return (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {coachingDashboard.noTranscript}
        </Typography>
      );
    case 'pending':
    case 'processing':
      return (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {coachingDashboard.processing}
        </Typography>
      );
    case 'completed':
      return (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {'\u2014'}
        </Typography>
      );
    default: {
      const _exhaustive: never = session.recording.status;

      console.warn('Unknown session recording status, falling back to em-dash', _exhaustive);

      return (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {'\u2014'}
        </Typography>
      );
    }
  }
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  padding: `${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-none']}`,
});

const emptyStateSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-xl'],
  textAlign: 'center',
});

const emptyStateTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/500'],
});

const errorTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Error/600'],
});

const paginationSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiTablePagination-toolbar': {
    paddingLeft: backrs.spacing['spacing-3xl'],
    paddingRight: backrs.spacing['spacing-xl'],
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    color: backrs.colors['Colors/Text/text-quaternary'],
  },
});

const productSubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const monospaceTextSx: SxProps<Theme> = {
  fontFamily: 'monospace',
};

const mutedTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const viewLinkSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/500'],
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
});
